import { Reducer } from 'redux';
import { update } from '../helpers/immutabilityHelper'
import * as Dtos from '../dtos/Tmd.dtos';

import {
    ACTION_TRIALWEBSITE_LOAD_BY_TRIAL_ID,
    ACTION_TRIALWEBSITE_LOAD_BY_ID,
    ACTION_TRIALWEBSITE_LOAD_FAILURE,
    ACTION_TRIALWEBSITE_LOAD_SUCCESS,

    ACTION_TRIALWEBSITE_SAVE,
    ACTION_TRIALWEBSITE_SAVE_SUCCESS,
    ACTION_TRIALWEBSITE_SAVE_FAILURE,

    ACTION_TRIALWEBSITE_VALIDATE,
    ACTION_TRIALWEBSITE_VALIDATE_SUCCESS,
    ACTION_TRIALWEBSITE_VALIDATE_FAILURE,

    ACTION_TRIALWEBSITE_CREATE,
    ACTION_TRIALWEBSITE_CREATE_SUCCESS,
    ACTION_TRIALWEBSITE_CREATE_FAILURE,

    ACTION_TRIALWEBSITE_SYNC,
    ACTION_TRIALWEBSITE_SYNC_SUCCESS,
    ACTION_TRIALWEBSITE_SYNC_FAILURE,

    ACTION_TRIALWEBSITE_CLEAR,
    ACTION_TRIALWEBSITE_FILTER,

    TrialWebsiteAction
} from '../actions/trialWebsite';
import { RequestState } from "../enumerations/RequestState";
import { IRequestState, IFormFilter } from "./index";
import { updateFailureIndexToId } from "../helpers/formErrorHelper";

export interface ITrialWebsiteState {
    formData: Dtos.TrialWebsite | Dtos.TrialWebsite[],
    formState: Dtos.FormState,
    formProperties: Dtos.FormProperty[],
    validationFailures: Dtos.ResponseError[];
    lookups: Dtos.Lookup[],
    loadState: IRequestState;
    saveState: IRequestState;
    validateState: IRequestState;
    updateState: IRequestState;
    createState: IRequestState;
    formFilter: IFormFilter;
    formFilteredData: Dtos.TrialWebsite[];
    formLength: number;
}

const initialState: ITrialWebsiteState = {
    formData: undefined,
    formState: undefined,
    formProperties: undefined,
    validationFailures: undefined,
    lookups: undefined,
    loadState: {
        status: RequestState.None
    } as IRequestState,
    saveState: {
        status: RequestState.None
    } as IRequestState,
    validateState: {
        status: RequestState.None
    } as IRequestState,
    updateState: {
        status: RequestState.None
    } as IRequestState,
    createState: {
        status: RequestState.None
    } as IRequestState,
    formFilter: {
        search: "",
        showInActive: false
    },
    formFilteredData: [],
    formLength: 0
};


function loading(state: ITrialWebsiteState, _isLoading: boolean): ITrialWebsiteState {
    const newState: ITrialWebsiteState = update(state, {
        loadState: {
            $set: {
                status: RequestState.Pending
            } as IRequestState
        }
    });

    return newState;
}

function loadSuccess(
    state: ITrialWebsiteState,
    formData: Dtos.TrialWebsite | Dtos.TrialWebsite[],
    formState: Dtos.FormState,
    formProperties: Dtos.FormProperty[],
    lookups: Dtos.Lookup[],
    responseStatus: Dtos.ResponseStatus
): ITrialWebsiteState {
    const newState: ITrialWebsiteState = update(state, {
        formData: {
            $set: formData
        },
        formState: {
            $set: formState
        },
        formProperties: {
            $set: formProperties
        },
        lookups: {
            $set: lookups
        },
        loadState: {
            $set: {
                status: RequestState.Success,
                errorCode: responseStatus ? responseStatus.errorCode : undefined,
                errorMessage: responseStatus ? responseStatus.message : undefined,
                errors: responseStatus ? responseStatus.errors : undefined,
                meta: responseStatus ? responseStatus.meta : undefined
            } as IRequestState
        },
        validationFailures: {
            $set: responseStatus ? updateFailureIndexToId(responseStatus.errors, formData) : undefined
        }
    });

    return filter(newState, state.formFilter);
}

function loadFailure(state: ITrialWebsiteState, responseStatus: Dtos.ResponseStatus): ITrialWebsiteState {
    const newState: ITrialWebsiteState = update(state, {
        loadState: {
            $set: {
                status: RequestState.Failure,
                errorCode: responseStatus.errorCode,
                errorMessage: responseStatus.message,
                errors: responseStatus.errors
            } as IRequestState
        }
    });

    return newState;
}

function filter(state: ITrialWebsiteState, formFilter: IFormFilter): ITrialWebsiteState {

    const items: Dtos.TrialWebsite[] | undefined =
        state.formData instanceof Array ?
            state.formData :
            undefined;

    const formLength = items ? items.length : 0;

    const formFilteredData = items ? items.filter(p => (p.active == true || formFilter.showInActive) &&
        (
            formFilter.search == "" ||
            p.registered.toLowerCase().indexOf(formFilter.search.toLowerCase()) > -1
        )) : undefined;

    const newState: ITrialWebsiteState = update(state, {
        formFilter: {
            $set: formFilter
        },
        formFilteredData: {
            $set: formFilteredData
        },
        formLength: {
            $set: formLength
        }
    });

    return newState;
}

function clear(state: ITrialWebsiteState, propertiesToClear?: string[]): ITrialWebsiteState {

    if (!propertiesToClear) {
        const newState: ITrialWebsiteState = update(initialState, {});
        return newState;
    }

    let stateModifier: any = {};

    propertiesToClear.forEach(propertyToClear => {
        stateModifier[propertyToClear] = { $set: initialState[propertyToClear] }
    });

    const newState = update(state, stateModifier);

    return newState;
}

function saving(state: ITrialWebsiteState, _isSaving: boolean): ITrialWebsiteState {
    const newState: ITrialWebsiteState = update(state, {
        saveState: {
            $set: {
                status: RequestState.Pending
            } as IRequestState
        }
    });

    return newState;
}

function saveSuccess(
    state: ITrialWebsiteState,
    formData: Dtos.TrialWebsite | Dtos.TrialWebsite[],
    formState: Dtos.FormState,
    responseStatus: Dtos.ResponseStatus
): ITrialWebsiteState {
    const newState: ITrialWebsiteState = update(state, {
        formData: {
            $set: formData
        },
        formState: {
            $set: formState
        },
        saveState: {
            $set: {
                status: RequestState.Success,
                errorCode: responseStatus ? responseStatus.errorCode : undefined,
                errorMessage: responseStatus ? responseStatus.message : undefined,
                errors: responseStatus ? responseStatus.errors : undefined,
                meta: responseStatus ? responseStatus.meta : undefined
            } as IRequestState
        },
        validationFailures: {
            $set: responseStatus ? updateFailureIndexToId(responseStatus.errors, formData) : undefined
        }
    });

    return newState;
}

function saveFailure(state: ITrialWebsiteState, responseStatus: Dtos.ResponseStatus): ITrialWebsiteState {
    const newState: ITrialWebsiteState = update(state, {
        saveState: {
            $set: {
                status: RequestState.Failure,
                errorCode: responseStatus.errorCode,
                errorMessage: responseStatus.message,
                errors: responseStatus.errors,
                meta: responseStatus.meta
            } as IRequestState
        },
        validationFailures: {
            $set: responseStatus ? updateFailureIndexToId(responseStatus.errors, state.formData) : undefined
        }
    });

    return newState;
}

function validating(state: ITrialWebsiteState, _isValidating: boolean): ITrialWebsiteState {
    const newState: ITrialWebsiteState = update(state, {
        validateState: {
            $set: {
                status: RequestState.Pending
            } as IRequestState
        }
    });

    return newState;
}

function validateSuccess(
    state: ITrialWebsiteState,
    formData: Dtos.TrialWebsite | Dtos.TrialWebsite[],
    formState: Dtos.FormState,
    responseStatus: Dtos.ResponseStatus
): ITrialWebsiteState {
    const newState: ITrialWebsiteState = update(state, {
        formData: {
            $set: formData
        },
        formState: {
            $set: formState
        },
        validateState: {
            $set: {
                status: RequestState.Success,
                errorCode: responseStatus ? responseStatus.errorCode : undefined,
                errorMessage: responseStatus ? responseStatus.message : undefined,
                errors: responseStatus ? responseStatus.errors : undefined,
                meta: responseStatus ? responseStatus.meta : undefined
            } as IRequestState
        },
        validationFailures: {
            $set: responseStatus ? updateFailureIndexToId(responseStatus.errors, formData) : undefined
        }
    });

    return newState;
}

function validateFailure(state: ITrialWebsiteState, responseStatus: Dtos.ResponseStatus): ITrialWebsiteState {
    const newState: ITrialWebsiteState = update(state, {
        validateState: {
            $set: {
                status: RequestState.Failure,
                errorCode: responseStatus.errorCode,
                errorMessage: responseStatus.message,
                errors: responseStatus.errors,
                meta: responseStatus.meta
            } as IRequestState
        },
        validationFailures: {
            $set: responseStatus ? updateFailureIndexToId(responseStatus.errors, state.formData) : undefined
        }
    });

    return newState;
}

function creating(state: ITrialWebsiteState, _isCreating: boolean): ITrialWebsiteState {
    const newState: ITrialWebsiteState = update(state, {
        createState: {
            $set: {
                status: RequestState.Pending
            } as IRequestState
        }
    });

    return newState;
}

function createSuccess(
    state: ITrialWebsiteState,
    formData: Dtos.TrialWebsite,
    formState: Dtos.FormState,
    formProperties: Dtos.FormProperty[],
    lookups: Dtos.Lookup[]
): ITrialWebsiteState {
    const newState: ITrialWebsiteState = update(state, {
        formData: {
            $set: formData
        },
        formState: {
            $set: formState
        },
        formProperties: {
            $set: formProperties
        },
        lookups: {
            $set: lookups
        },
        createState: {
            $set: {
                status: RequestState.Success
            } as IRequestState
        }
    });

    return newState;
}

function createFailure(state: ITrialWebsiteState, responseStatus: Dtos.ResponseStatus): ITrialWebsiteState {
    const newState: ITrialWebsiteState = update(state, {
        createState: {
            $set: {
                status: RequestState.Failure,
                errorCode: responseStatus.errorCode,
                errorMessage: responseStatus.message,
                errors: responseStatus.errors
            } as IRequestState
        }
    });

    return newState;
}

const TrialWebsiteReducer: Reducer<ITrialWebsiteState> = (state: ITrialWebsiteState = initialState, action: TrialWebsiteAction) => {
    switch (action.type) {
        case ACTION_TRIALWEBSITE_LOAD_BY_TRIAL_ID:
        case ACTION_TRIALWEBSITE_LOAD_BY_ID:
            return loading(state, true);
        case ACTION_TRIALWEBSITE_LOAD_SUCCESS:
            return loadSuccess(state, action.formData, action.formState, action.formProperties, action.lookups, action.responseStatus);
        case ACTION_TRIALWEBSITE_LOAD_FAILURE:
            return loadFailure(state, action.responseStatus);
        case ACTION_TRIALWEBSITE_CLEAR:
            return clear(state, action.propertiesToClear);
        case ACTION_TRIALWEBSITE_SAVE:
            return saving(state, true);
        case ACTION_TRIALWEBSITE_SAVE_SUCCESS:
            return saveSuccess(state, action.formData, action.formState, action.responseStatus);
        case ACTION_TRIALWEBSITE_SAVE_FAILURE:
            return saveFailure(state, action.responseStatus);
        case ACTION_TRIALWEBSITE_VALIDATE:
            return validating(state, true);
        case ACTION_TRIALWEBSITE_VALIDATE_SUCCESS:
            return validateSuccess(state, action.formData, action.formState, action.responseStatus);
        case ACTION_TRIALWEBSITE_VALIDATE_FAILURE:
            return validateFailure(state, action.responseStatus);
        case ACTION_TRIALWEBSITE_CREATE:
            return creating(state, true);
        case ACTION_TRIALWEBSITE_CREATE_SUCCESS:
            return createSuccess(state, action.formData, action.formState, action.formProperties, action.lookups);
        case ACTION_TRIALWEBSITE_CREATE_FAILURE:
            return createFailure(state, action.responseStatus);
        case ACTION_TRIALWEBSITE_FILTER:
            return filter(state, action.formFilter);
        case ACTION_TRIALWEBSITE_SYNC:
            return loading(state, true);
        case ACTION_TRIALWEBSITE_SYNC_SUCCESS:
            return loadSuccess(state, action.formData, action.formState, action.formProperties, action.lookups, action.responseStatus);
        case ACTION_TRIALWEBSITE_SYNC_FAILURE:
            return loadFailure(state, action.responseStatus);

    }

    return state;
}

export default TrialWebsiteReducer;